import fetch from '../core/fetch';
import { parseResponse } from '../core/fetch/response';
import AuthorizationHeader from './authorization-header';

export default class Progress {
  static appendQueryOfIDs(listOfID) {
    let queryOfIDs = '';
    if (Array.isArray(listOfID)) {
      queryOfIDs = `?ids=${listOfID.join(',')}`;
    }
    return queryOfIDs;
  }

  static getParamList(paramArr, joinStr = ',', prefixStr = '') {
    let paramStr = '';
    if (Array.isArray(paramArr)) {
      paramStr = `?${prefixStr}${paramArr.join(joinStr)}${joinStr}`;
    }
    return paramStr;
  }

  static getProgressURL() {
    return `${process.env.REACT_APP_CLIENT_LIBRARY_API}/progress`;
  }

  static getPathURL(listOfID = null) {
    return `${process.env.REACT_APP_CLIENT_LIBRARY_API}/paths-progress${Progress.appendQueryOfIDs(
      listOfID,
    )}`;
  }

  static getCourseURL(listOfID = null) {
    return `${process.env.REACT_APP_CLIENT_LIBRARY_API}/courses-progress${Progress.appendQueryOfIDs(
      listOfID,
    )}`;
  }

  static getLessonURL(listOfID = null) {
    return `${process.env.REACT_APP_CLIENT_LIBRARY_API}/lessons-progress${Progress.appendQueryOfIDs(
      listOfID,
    )}`;
  }

  static getLessonStandaloneURL() {
    return `${process.env.REACT_APP_CLIENT_LIBRARY_API}/lessons-progress/standalone`;
  }

  static getCompleteLessonsURL() {
    return `${process.env.REACT_APP_CLIENT_LIBRARY_API}/progress/lessons`;
  }

  static async getPath(listOfID = null) {
    const url = this.getPathURL(listOfID);
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getCourses(listOfID = null) {
    const url = this.getCourseURL(listOfID);
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getLessons(listOfID = null) {
    const url = this.getLessonURL(listOfID);
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getProgress() {
    const url = this.getProgressURL();
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getProgressLevelsCompleted(pathArr) {
    const baseUrl = this.getProgressURL();
    const stringPathArr = encodeURI(JSON.stringify(pathArr));
    const paramStr = `?path-levels-completed=${stringPathArr}`;
    const url = `${baseUrl}${paramStr}`;
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getProgressRecent(lessons = 0, exercises = 0, songs = 0, chords = 0, riffs = 0) {
    const baseUrl = this.getProgressURL();
    const paramStr = `?recent-lessons=${lessons}&recent-lessons-exercise=${exercises}&recent-courses-song=${songs}&recent-lessons-chord=${chords}&recent-courses-riff=${riffs}`; // eslint-disable-line
    const url = `${baseUrl}${paramStr}`;
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getRecentlyViewed(count) {
    const baseUrl = this.getProgressURL();
    const paramStr = `?recently-viewed=${count}`;
    const url = `${baseUrl}${paramStr}`;
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getLastActivity(count) {
    const baseUrl = this.getProgressURL();
    const paramStr = `?last-activity=${count}`;
    const url = `${baseUrl}${paramStr}`;
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getUpNext(nextCount) {
    const baseUrl = this.getProgressURL();
    const paramStr = `?up-next=${nextCount}`;
    const url = `${baseUrl}${paramStr}`;
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async getProgressCounts(
    progressArr = [
      'levels',
      'playlists',
      'chords',
      'skills',
      'songs-riffs',
      'courses',
      'lessons',
      'practice-sheets',
    ],
  ) {
    const baseUrl = this.getProgressURL();
    const paramStr = this.getParamList(progressArr, '-count=true&');
    const url = `${baseUrl}${paramStr.substring(0, paramStr.length - 1)}`;
    const headers = await AuthorizationHeader.getCognitoHeader();
    const response = await fetch(url, {
      method: 'GET',
      ...headers,
    });
    return parseResponse(response);
  }

  static async updatePath(paths) {
    const url = Progress.getPathURL();
    const headers = await AuthorizationHeader.getCognitoHeader();
    if (!headers.headers.Authorization) {
      return null;
    }
    const response = await fetch(url, {
      method: 'PUT',
      ...headers,
      body: JSON.stringify(paths),
    });
    return parseResponse(response);
  }

  static async updateCourse(courses) {
    const url = Progress.getCourseURL();
    const headers = await AuthorizationHeader.getCognitoHeader();
    if (!headers.headers.Authorization) {
      return null;
    }
    const response = await fetch(url, {
      method: 'PUT',
      ...headers,
      body: JSON.stringify(courses),
    });
    return parseResponse(response);
  }

  static async updateLesson(lessons) {
    const url = Progress.getLessonURL();
    const headers = await AuthorizationHeader.getCognitoHeader();
    if (!headers.headers.Authorization) {
      return null;
    }
    const response = await fetch(url, {
      method: 'PUT',
      ...headers,
      body: JSON.stringify(lessons),
    });
    return parseResponse(response);
  }

  static async updateLessonStandalone(lessonId, lessonType, activityType = 'lesson') {
    const url = Progress.getLessonStandaloneURL();
    const headers = await AuthorizationHeader.getCognitoHeader();
    if (!headers.headers.Authorization) {
      return null;
    }
    const response = await fetch(url, {
      method: 'PUT',
      ...headers,
      body: JSON.stringify([
        {
          'activity-type': activityType,
          'lesson-id': lessonId,
          'lesson-type': lessonType,
        },
      ]),
    });
    return parseResponse(response);
  }

  static async updateLessonCompletedProgress(options) {
    const headers = await AuthorizationHeader.getCognitoHeader();
    if (!headers.headers.Authorization || !options || !options[0]) {
      return null;
    }
    const url = Progress.getCompleteLessonsURL();
    const response = await fetch(url, {
      method: 'PUT',
      ...headers,
      body: JSON.stringify([...options]),
    });
    return parseResponse(response);
  }

  static mapUpdatePathPayload(
    pathID,
    playlistID,
    courseID,
    lessonID,
    style,
    instrument,
    courseType,
    lessonType,
    activityType = 'lesson',
  ) {
    return {
      'path-id': pathID,
      'playlist-id': playlistID,
      'course-id': courseID,
      'lesson-id': lessonID,
      style,
      instrument,
      'course-type': courseType,
      'lesson-type': lessonType,
      'activity-type': activityType,
    };
  }

  static mapUpdateCoursePayload(
    courseID,
    lessonID,
    type,
    lessonType,
    instrument = 'guitar',
    activityType = 'lesson',
  ) {
    return {
      'activity-type': activityType,
      'course-id': courseID,
      instrument,
      'lesson-id': lessonID,
      type,
      'lesson-type': lessonType,
    };
  }

  static mapUpdateLessonPayload(
    lessonID,
    lessonType,
    videoWatchedSeconds,
    videoWatchedPercent = 0,
    instrument = 'guitar',
  ) {
    return {
      'activity-type': 'lesson',
      instrument,
      'lesson-id': lessonID,
      'lesson-type': lessonType,
      'video-watched-seconds': videoWatchedSeconds,
      'video-watched-percent': videoWatchedPercent,
    };
  }
}
